import { useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import SiteHeader from '../components/SiteHeader';
import Footer from '../components/Footer';
import useAuthModal from '../hooks/useAuthModal';
import { getPillarBySlug } from '../config/pillars';

export default function PillarScreen() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const authModal = useAuthModal();
  const [openFaq, setOpenFaq] = useState(null);

  const pillarSlug = pathname.replace(/^\/|\/$/g, '');
  const pillar = useMemo(() => getPillarBySlug(pillarSlug), [pillarSlug]);

  if (!pillar) {
    return (
      <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
        <SiteHeader authModal={authModal} />
        <main className="max-w-4xl mx-auto px-6 py-16 text-center">
          <h1 className="text-3xl font-semibold">Guide not found</h1>
          <p className="text-gray-500 mt-3">Browse all guides on the blog or start a free mock interview.</p>
          <button
            type="button"
            onClick={() => navigate('/blog')}
            className="btn-orange mt-6 px-6 py-3 text-sm rounded-2xl"
          >
            Back to blog
          </button>
        </main>
        <Footer />
      </div>
    );
  }

  const toggleFaq = (i) => setOpenFaq(openFaq === i ? null : i);

  return (
    <div className="min-h-screen bg-[#F6F7FB] text-[#1E293B]">
      <SiteHeader authModal={authModal} />

      <main className="max-w-4xl mx-auto px-6 py-12">
        {/* HERO */}
        <header className="text-center mb-10">
          <span className="inline-block bg-orange-50 text-orange-600 text-xs font-semibold px-3 py-1 rounded-full mb-4 uppercase tracking-wide">
            {pillar.badge}
          </span>
          <h1 className="text-4xl md:text-5xl font-semibold leading-tight tracking-tight">
            {pillar.h1}
          </h1>
          <p className="text-gray-500 mt-5 max-w-3xl mx-auto text-lg leading-relaxed">
            {pillar.intro}
          </p>
        </header>

        {/* DEFINITION BLOCK — for AI citation */}
        <aside className="bg-white border-l-4 border-orange-500 rounded-2xl shadow-sm p-6 md:p-7 mb-10">
          <p className="font-semibold text-lg mb-2">{pillar.definitionBlock.question}</p>
          <p className="text-gray-700 leading-relaxed">{pillar.definitionBlock.answer}</p>
        </aside>

        {/* KEY TAKEAWAYS */}
        <section className="bg-orange-50/60 rounded-2xl p-6 md:p-7 mb-12">
          <h2 className="text-base font-semibold text-orange-700 uppercase tracking-wide mb-3">
            Key Takeaways
          </h2>
          <ul className="space-y-2">
            {pillar.keyTakeaways.map((item, i) => (
              <li key={i} className="flex gap-3 text-[15px] text-gray-800">
                <span className="text-orange-600 mt-1">▸</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </section>

        {/* INLINE CTA — first */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 md:p-7 text-center mb-12">
          <p className="text-sm text-gray-500 mb-3">Skip ahead to practice</p>
          <Link
            to={pillar.cta.href}
            className="btn-orange px-8 py-3 rounded-2xl text-sm font-semibold inline-block"
          >
            Start free AI mock interview →
          </Link>
          <p className="text-xs text-gray-400 mt-2">3 sessions · no credit card</p>
        </div>

        {/* SECTIONS — each links to a spoke article */}
        {pillar.sections.map((section, i) => (
          <section key={i} className="mb-12">
            <h2 className="text-2xl md:text-3xl font-semibold mb-5 leading-snug">
              {section.h2}
            </h2>
            {section.body.map((paragraph, p) => (
              <p key={p} className="text-gray-700 leading-relaxed mb-4 text-[16px]">
                {paragraph}
              </p>
            ))}
            {section.spoke && (
              <Link
                to={`/blog/${section.spoke.slug}`}
                className="block bg-white rounded-2xl shadow-sm border border-gray-200 p-5 mt-5 hover:border-orange-300 hover:shadow-md transition group"
              >
                <span className="text-xs uppercase font-semibold text-orange-600 tracking-wide">
                  Deep dive
                </span>
                <p className="font-semibold text-lg text-[#1E293B] mt-1 group-hover:text-orange-700 transition">
                  {section.spoke.title}
                </p>
                <p className="text-gray-500 mt-1 text-[15px]">
                  {section.spoke.description}
                </p>
                <span className="text-orange-600 text-sm font-medium mt-2 inline-block">
                  Read the full article →
                </span>
              </Link>
            )}
          </section>
        ))}

        {/* FAQ */}
        {pillar.faq?.length > 0 && (
          <section className="mb-12">
            <h2 className="text-2xl md:text-3xl font-semibold mb-5">Frequently asked questions</h2>
            <div className="space-y-3">
              {pillar.faq.map((item, i) => (
                <div key={i} className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => toggleFaq(i)}
                    className="w-full text-left px-6 py-4 flex justify-between items-center gap-4"
                  >
                    <span className="font-semibold text-[16px]">{item.q}</span>
                    <span className="text-orange-500 text-xl flex-shrink-0">
                      {openFaq === i ? '−' : '+'}
                    </span>
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5 text-gray-700 leading-relaxed">
                      {item.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}

        {/* FINAL CTA */}
        <section className="bg-[#1E293B] rounded-[32px] px-6 py-12 md:px-12 md:py-14 text-center text-white shadow-lg mb-8">
          <h2 className="text-2xl md:text-3xl font-semibold">{pillar.cta.headline}</h2>
          <p className="mt-3 text-blue-100 max-w-2xl mx-auto">{pillar.cta.sub}</p>
          <Link
            to={pillar.cta.href}
            className="btn-orange mt-7 px-8 py-3 rounded-2xl text-sm font-semibold inline-block"
          >
            {pillar.cta.action} →
          </Link>
        </section>
      </main>

      <Footer />
    </div>
  );
}
